import { useState, useEffect } from 'react';
import { ArrowLeft, Bell, Lock, Shield, Eye, Globe, Heart, MessageCircle, HelpCircle, LogOut, ChevronRight, Moon, Sun, Palette, User, FileText, Trash2, Image, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import axios from 'axios';
import { useAuthContext } from '../contexts/AuthContext';
import { BACKEND_CONFIG } from '../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface SettingsPageModernProps {
  onBack: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToEditProfile?: () => void;
  onNavigateToChangePassword?: () => void;
  onNavigateToHelp?: () => void;
  onNavigateToTerms?: () => void;
  onLogout?: () => void;
}

type SettingsSection = 'account' | 'privacy' | 'notifications' | 'support';

export default function SettingsPageModern({ 
  onBack, 
  onNavigateToPrivacy, 
  onNavigateToEditProfile,
  onNavigateToChangePassword,
  onNavigateToHelp,
  onNavigateToTerms,
  onLogout
}: SettingsPageModernProps) {
  const { user } = useAuthContext();
  const [activeSection, setActiveSection] = useState<SettingsSection>('account');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteRequestStatus, setDeleteRequestStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Privacy settings state
  const [profileVisibility, setProfileVisibility] = useState(true);
  const [photoVisibility, setPhotoVisibility] = useState<'all' | 'connected' | 'premium'>('all');
  const [showFullName, setShowFullName] = useState(true);
  
  // Notification settings state
  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [chatNotifications, setChatNotifications] = useState(true);
  
  // Load user preferences on mount
  useEffect(() => {
    loadUserPreferences();
    checkDeleteRequestStatus();
  }, []);
  
  const loadUserPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Load general preferences
      const { data } = await axios.get(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data.success && data.data.user.preferences) {
        const prefs = data.data.user.preferences;
        setProfileVisibility(prefs.profileVisibility ?? true);
        setShowFullName(prefs.showFullName ?? true);
        setPushNotifications(prefs.pushNotifications ?? true);
        setEmailNotifications(prefs.emailNotifications ?? true);
        setChatNotifications(prefs.chatNotifications ?? true);
      }
      
      // Load photo visibility from dedicated endpoint
      try {
        const photoResponse = await axios.get(`${API_URL}/photo-privacy/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (photoResponse.data.success) {
          setPhotoVisibility(photoResponse.data.data.photoVisibility || 'all');
        }
      } catch (photoError) {
        // If photo privacy endpoint fails, fallback to general preferences
        if (data.success && data.data.user.preferences?.photoVisibility) {
          setPhotoVisibility(data.data.user.preferences.photoVisibility);
        }
      }
    } catch (error) {
      // Silently handle errors when backend not accessible
    }
  };
  
  const updatePreference = async (key: string, value: boolean | string) => {
    try {
      setLoading(true);
      
      // Update local state immediately for better UX
      switch(key) {
        case 'profileVisibility':
          setProfileVisibility(value as boolean);
          break;
        case 'photoVisibility':
          setPhotoVisibility(value as 'all' | 'connected' | 'premium');
          break;
        case 'showFullName':
          setShowFullName(value as boolean);
          break;
        case 'pushNotifications':
          setPushNotifications(value as boolean);
          break;
        case 'emailNotifications':
          setEmailNotifications(value as boolean);
          break;
        case 'chatNotifications':
          setChatNotifications(value as boolean);
          break;
      }
      
      const token = localStorage.getItem('token');
      
      // Use dedicated endpoint for photo visibility
      if (key === 'photoVisibility') {
        await axios.put(`${API_URL}/photo-privacy/settings`, {
          photoVisibility: value
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Use general preferences endpoint for other settings
        await axios.put(`${API_URL}/users/me`, {
          preferences: {
            [key]: value
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Setting updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
      
      // Revert local state on error
      loadUserPreferences();
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      setLoading(true);
      if (onLogout) {
        await onLogout();
      }
    } catch (error) {
      toast.error('Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/delete-account/request`, {
        reason: deleteReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data.success) {
        toast.success('Delete request submitted. Admin will review it shortly.');
        setShowDeleteDialog(false);
        setDeleteReason('');
        await checkDeleteRequestStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit delete request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteRequest = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.delete(`${API_URL}/delete-account/cancel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data.success) {
        toast.success('Delete request cancelled');
        setDeleteRequestStatus(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel request');
    } finally {
      setLoading(false);
    }
  };

  const checkDeleteRequestStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/delete-account/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setDeleteRequestStatus(data.data);
      }
    } catch (error) {
      // Silent fail - no pending request
      setDeleteRequestStatus(null);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'account':
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div>
              <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
              <div className="space-y-3">
                <button 
                  onClick={onNavigateToEditProfile}
                  className="w-full flex items-center gap-3 p-4 bg-white border rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="bg-blue-50 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                    <User className="size-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Edit Profile</div>
                    <div className="text-xs text-gray-500">Update your personal information</div>
                  </div>
                  <ChevronRight className="size-4 text-gray-400" />
                </button>
                <button 
                  onClick={onNavigateToChangePassword}
                  className="w-full flex items-center gap-3 p-4 bg-white border rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="bg-amber-50 p-2 rounded-lg group-hover:bg-amber-100 transition-colors">
                    <Lock className="size-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Change Password</div>
                    <div className="text-xs text-gray-500">Update your security credentials</div>
                  </div>
                  <ChevronRight className="size-4 text-gray-400" />
                </button>
                
                {user?.referredBy && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <Heart className="size-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-green-900">Joined via Referral</div>
                        <div className="text-xs text-green-700 mt-1">
                          Referral Code: <span className="font-mono font-semibold">{user.referredBy}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Delete Account Section */}
            <div>
               <h3 className="text-lg font-semibold mb-4 text-rose-600">Danger Zone</h3>
               
               {deleteRequestStatus ? (
                 <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50">
                   <h4 className="font-medium mb-1 text-yellow-900">Delete Request Pending</h4>
                   <p className="text-sm text-yellow-700 mb-2">
                     Your delete request is under admin review. You will be notified once it's processed.
                   </p>
                   <p className="text-xs text-yellow-600 mb-4">
                     Requested on: {new Date(deleteRequestStatus.createdAt).toLocaleDateString()}
                   </p>
                   <Button 
                     variant="outline" 
                     size="sm"
                     onClick={handleCancelDeleteRequest}
                     disabled={loading}
                   >
                     Cancel Request
                   </Button>
                 </div>
               ) : (
                 <div className="border border-rose-100 rounded-xl p-4 bg-rose-50">
                    <h4 className="font-medium mb-1">Delete Account</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Submit a request to delete your account. Admin will review it.
                    </p>
                    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="size-4 mr-2" />
                          Request Account Deletion
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Request Account Deletion?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Your request will be sent to admin for review. If approved, your account will be deactivated.
                            You can recover your account by logging in again after approval.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        
                        <div className="space-y-2">
                          <Label htmlFor="deleteReason">Reason (Optional)</Label>
                          <Textarea
                            id="deleteReason"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder="Help us understand why you're leaving..."
                            rows={3}
                          />
                        </div>
                        
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteAccount} 
                            className="bg-rose-600 hover:bg-rose-700"
                            disabled={loading}
                          >
                            {loading ? 'Submitting...' : 'Submit Request'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                 </div>
               )}
            </div>
          </div>
        );
      case 'privacy':
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <h3 className="text-lg font-semibold mb-4">Privacy Controls</h3>
            <div className="space-y-4">
              {/* Profile Visibility */}
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-50 p-2 rounded-lg">
                    <Eye className="size-5 text-purple-600" />
                  </div>
                  <div>
                    <Label className="text-base">Profile Visibility</Label>
                    <p className="text-sm text-gray-500">Make profile visible to all users</p>
                  </div>
                </div>
                <Switch 
                  checked={profileVisibility}
                  onCheckedChange={(value) => updatePreference('profileVisibility', value)}
                />
              </div>

              {/* Photo Visibility */}
              <div className="p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-rose-50 p-2 rounded-lg">
                    <Image className="size-5 text-rose-600" />
                  </div>
                  <div>
                    <Label className="text-base">Photo Visibility</Label>
                    <p className="text-sm text-gray-500">Control who can see your photos</p>
                  </div>
                </div>
                <RadioGroup value={photoVisibility} onValueChange={(value) => updatePreference('photoVisibility', value)}>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <RadioGroupItem value="all" id="photo-all" />
                      <Label htmlFor="photo-all" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Globe className="size-4 text-blue-600" />
                          <span className="font-medium">Show to All</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Everyone can see your photos</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <RadioGroupItem value="connected" id="photo-connected" />
                      <Label htmlFor="photo-connected" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Users className="size-4 text-green-600" />
                          <span className="font-medium">Show to Connected Users</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Only users you've accepted interests from</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <RadioGroupItem value="premium" id="photo-premium" />
                      <Label htmlFor="photo-premium" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Crown className="size-4 text-amber-600" />
                          <span className="font-medium">Show to Premium Members</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Only premium subscribers can see</p>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> Contact details are only visible to users with active plans and available contact views. Phone numbers are automatically shown only to users who have sent interests.
                  </p>
                </div>
              </div>

              {/* Full Name */}
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <User className="size-5 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-base">Show Full Name</Label>
                    <p className="text-sm text-gray-500">Display your full name on profile</p>
                  </div>
                </div>
                <Switch 
                  checked={showFullName}
                  onCheckedChange={(value) => updatePreference('showFullName', value)}
                />
              </div>
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-50 p-2 rounded-lg">
                    <Bell className="size-5 text-amber-600" />
                  </div>
                  <div>
                    <Label className="text-base">Push Notifications</Label>
                    <p className="text-sm text-gray-500">Get notified about new matches instantly</p>
                  </div>
                </div>
                <Switch 
                  checked={pushNotifications}
                  onCheckedChange={(value) => updatePreference('pushNotifications', value)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-rose-50 p-2 rounded-lg">
                    <Bell className="size-5 text-rose-600" />
                  </div>
                  <div>
                    <Label className="text-base">Email Notifications</Label>
                    <p className="text-sm text-gray-500">Receive daily digest and updates</p>
                  </div>
                </div>
                <Switch 
                  checked={emailNotifications}
                  onCheckedChange={(value) => updatePreference('emailNotifications', value)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-white border rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <MessageCircle className="size-5 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-base">Chat Notifications</Label>
                    <p className="text-sm text-gray-500">When someone sends you a message</p>
                  </div>
                </div>
                <Switch 
                  checked={chatNotifications}
                  onCheckedChange={(value) => updatePreference('chatNotifications', value)}
                />
              </div>
            </div>
          </div>
        );
      case 'support':
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
             <h3 className="text-lg font-semibold mb-4">Support & Legal</h3>
             <div className="space-y-3">
                <button 
                  onClick={onNavigateToHelp}
                  className="w-full flex items-center gap-3 p-4 bg-white border rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="bg-teal-50 p-2 rounded-lg group-hover:bg-teal-100 transition-colors">
                    <HelpCircle className="size-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Help & Support</div>
                    <div className="text-xs text-gray-500">FAQs and Customer Care</div>
                  </div>
                  <ChevronRight className="size-4 text-gray-400" />
                </button>
                <button 
                  onClick={onNavigateToTerms}
                  className="w-full flex items-center gap-3 p-4 bg-white border rounded-xl hover:bg-gray-50 transition-colors text-left group"
                >
                  <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-gray-200 transition-colors">
                    <FileText className="size-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Terms & Conditions</div>
                    <div className="text-xs text-gray-500">Read our terms of service</div>
                  </div>
                  <ChevronRight className="size-4 text-gray-400" />
                </button>
                <button 
                  className="w-full flex items-center gap-3 p-4 bg-white border rounded-xl hover:bg-gray-50 transition-colors text-left group"
                  onClick={onNavigateToPrivacy}
                >
                  <div className="bg-indigo-50 p-2 rounded-lg group-hover:bg-indigo-100 transition-colors">
                    <Shield className="size-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Privacy Policy</div>
                    <div className="text-xs text-gray-500">How we handle your data</div>
                  </div>
                  <ChevronRight className="size-4 text-gray-400" />
                </button>
             </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-rose-700 to-amber-600 text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-md flex-shrink-0">
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onBack}>
          <ArrowLeft className="size-6" />
        </Button>
        <span className="text-lg font-medium">Settings</span>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-gray-50">
        <div className="max-w-7xl mx-auto h-full md:p-6">
           <div className="h-full flex flex-col md:flex-row md:gap-6 md:bg-white md:rounded-2xl md:shadow-sm md:border md:overflow-hidden">
              {/* Sidebar (Desktop) / List (Mobile) */}
              <div className="md:w-1/4 bg-white border-r flex flex-col overflow-y-auto">
                 <div className="p-4 md:p-6 border-b">
                    <h2 className="font-semibold text-gray-500 uppercase text-xs tracking-wider">Settings Menu</h2>
                 </div>
                 <div className="p-2 space-y-1">
                    <button 
                       onClick={() => setActiveSection('account')}
                       className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === 'account' ? 'bg-rose-50 text-rose-700' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                       <User className="size-5" />
                       <span className="font-medium">Account</span>
                    </button>
                    <button 
                       onClick={() => setActiveSection('privacy')}
                       className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === 'privacy' ? 'bg-rose-50 text-rose-700' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                       <Lock className="size-5" />
                       <span className="font-medium">Privacy</span>
                    </button>
                    <button 
                       onClick={() => setActiveSection('notifications')}
                       className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === 'notifications' ? 'bg-rose-50 text-rose-700' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                       <Bell className="size-5" />
                       <span className="font-medium">Notifications</span>
                    </button>
                    <button 
                       onClick={() => setActiveSection('support')}
                       className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === 'support' ? 'bg-rose-50 text-rose-700' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                       <HelpCircle className="size-5" />
                       <span className="font-medium">Support</span>
                    </button>
                 </div>
                 
                 <div className="mt-auto p-4 border-t">
                    <Button variant="outline" className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" onClick={handleLogout}>
                       <LogOut className="size-4 mr-2" />
                       Logout
                    </Button>
                 </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-white">
                 {renderContent()}
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}
